import { useState } from 'react';
import { Icon } from './ui/Icon';
import { formatMoney } from '../utils/costFormatting';
import { describeCashSubLine } from '../utils/cashBreakdownLines';
import type { CashGroup, ShiftCashBreakdown } from '../types/shiftCashBreakdown';

// DETALLE DEL TURNO — explica de dónde sale el efectivo esperado, en idioma de mostrador.
// Todas las cifras y la clasificación efectivo/informativo vienen del backend (fuente única de
// verdad); aquí solo se presentan. Diseñado móvil primero: filas concepto/importe, sin tablas
// anchas, con el detalle de cada grupo desplegable a un toque. FI-22 · Entrega 3: bajo cada
// compra se listan sus insumos (cantidad, unidad y costo unitario) y bajo cada gasto su tipo.
// Revisión B-1 (2026-09-16): a quien no tiene financials.viewCosts el backend le manda cada grupo
// con items vacíos (solo total y conteo); aquí eso se pinta como grupo no desplegable, sin contador.

const formatTime = (iso: string | null, timeZone: string) =>
  (iso ? new Intl.DateTimeFormat('es-MX', { hour: 'numeric', minute: '2-digit', timeZone }).format(new Date(iso)) : '');

export function ShiftCashBreakdownPanel({ breakdown, onOpenReference }: {
  breakdown: ShiftCashBreakdown;
  onOpenReference?: (line: { kind: string; referenceId: string }) => void;
}) {
  const variance = breakdown.variance === null ? null : Number(breakdown.variance);
  const varianceTone = variance === null ? 'neutral' : Math.abs(variance) < 0.005 ? 'ok' : variance > 0 ? 'over' : 'short';

  return (
    <section aria-label="Detalle del turno" className="cash-breakdown">
      <div className="cash-breakdown__head">
        <h3>Detalle del turno</h3>
        <span>De dónde sale el efectivo que deberías tener en la caja.</span>
      </div>

      {!breakdown.breakdownAvailable && (
        <div className="alert alert--info">
          Este corte es anterior a que la app guardara el detalle del efectivo, así que solo se
          conservan el efectivo esperado, el contado y la diferencia. No es posible reconstruir de
          dónde salió cada peso sin inventar cifras.
        </div>
      )}

      {breakdown.detailMatchesSnapshot === false && (
        <div className="alert alert--warning">
          Algún movimiento cambió después de guardar este corte, así que el detalle de hoy ya no suma
          exactamente lo que se guardó. Los totales del corte se conservan tal como quedaron:
          {breakdown.snapshotDifferences.map((difference) => (
            <span className="cash-breakdown__diff" key={difference.concept}>
              {difference.concept}: se guardó {formatMoney(difference.snapshot)}, hoy suma {formatMoney(difference.recalculated)}.
            </span>
          ))}
        </div>
      )}

      {breakdown.breakdownAvailable && <>
      <div className="cash-rows">
        <div className="cash-row cash-row--opening">
          <span>Efectivo al iniciar</span>
          <strong>{formatMoney(breakdown.openingCash)}</strong>
        </div>
      </div>

      <CashSection
        title="Entradas de efectivo"
        sign="+"
        tone="in"
        total={breakdown.inflows.total}
        groups={breakdown.inflows.groups}
        timeZone={breakdown.timeZone}
        emptyLabel="No entró efectivo en este turno."
        onOpenReference={onOpenReference}
      />

      <CashSection
        title="Salidas de efectivo"
        sign="−"
        tone="out"
        total={breakdown.outflows.total}
        groups={breakdown.outflows.groups}
        timeZone={breakdown.timeZone}
        emptyLabel="No salió efectivo en este turno."
        onOpenReference={onOpenReference}
      />
      </>}

      <div className="cash-rows cash-rows--result">
        <div className="cash-row cash-row--expected">
          <span>Efectivo esperado</span>
          <strong>{formatMoney(breakdown.expectedCash)}</strong>
        </div>
        {breakdown.countedCash !== null && (
          <div className="cash-row">
            <span>Efectivo contado</span>
            <strong>{formatMoney(breakdown.countedCash)}</strong>
          </div>
        )}
        {variance !== null && (
          <div className={`cash-row cash-row--variance cash-row--${varianceTone}`}>
            <span>{varianceTone === 'ok' ? 'Sin diferencia' : varianceTone === 'over' ? 'Sobrante' : 'Faltante'}</span>
            <strong>{variance > 0 ? '+' : ''}{formatMoney(breakdown.variance!)}</strong>
          </div>
        )}
      </div>

      {breakdown.nonCash.groups.length > 0 && (
        <div className="cash-noncash">
          <div className="cash-noncash__head">
            <strong>No afectan el efectivo</strong>
            <span>Movimientos del turno pagados con tarjeta, transferencia u otro medio: son informativos y no cambian el efectivo esperado.</span>
          </div>
          {breakdown.nonCash.groups.map((group) => (
            <CashGroupRow group={group} key={group.key} timeZone={breakdown.timeZone} sign="" tone="info" onOpenReference={onOpenReference} />
          ))}
        </div>
      )}
    </section>
  );
}

function CashSection({ title, sign, tone, total, groups, timeZone, emptyLabel, onOpenReference }: {
  title: string;
  sign: string;
  tone: 'in' | 'out';
  total: string;
  groups: CashGroup[];
  timeZone: string;
  emptyLabel: string;
  onOpenReference?: (line: { kind: string; referenceId: string }) => void;
}) {
  return (
    <div className={`cash-section cash-section--${tone}`}>
      <div className="cash-section__head">
        <h4>{title}</h4>
        <strong>{sign}{formatMoney(total)}</strong>
      </div>
      {groups.length === 0
        ? <p className="cash-section__empty">{emptyLabel}</p>
        : groups.map((group) => <CashGroupRow group={group} key={group.key} timeZone={timeZone} sign={sign} tone={tone} onOpenReference={onOpenReference} />)}
    </div>
  );
}

function CashGroupRow({ group, timeZone, sign, tone, onOpenReference }: {
  group: CashGroup;
  timeZone: string;
  sign: string;
  tone: string;
  onOpenReference?: (line: { kind: string; referenceId: string }) => void;
}) {
  const [open, setOpen] = useState(false);
  const expandable = group.items.length > 0;
  return (
    <div className={`cash-group ${open ? 'cash-group--open' : ''}`}>
      <button
        aria-expanded={expandable ? open : undefined}
        className="cash-row cash-row--group"
        disabled={!expandable}
        onClick={() => expandable && setOpen((value) => !value)}
        type="button"
      >
        <span className="cash-group__label">
          {group.label}
          {expandable && <span className="cash-group__count">{group.count}</span>}
        </span>
        <strong className={`cash-group__total cash-group__total--${tone}`}>{sign}{formatMoney(group.total)}</strong>
        {expandable && <span aria-hidden="true" className="cash-group__chevron"><Icon name="chevron-right" size={14} /></span>}
      </button>
      {group.hint && !open && <span className="cash-group__hint">{group.hint}</span>}
      {open && (
        <ul className="cash-items">
          {group.items.map((item) => (
            <li className="cash-item" key={`${item.kind}-${item.referenceId ?? item.description}`}>
              <span className="cash-item__main">
                <span className="cash-item__time">{formatTime(item.occurredAt, timeZone)}</span>
                <span className="cash-item__desc">
                  {onOpenReference && item.referenceId
                    ? <button className="link-button" onClick={() => onOpenReference({ kind: item.kind, referenceId: item.referenceId! })} type="button">{item.description}</button>
                    : item.description}
                  {item.folio && <small>{item.folio}</small>}
                  {item.paymentLabel && <small>{item.paymentLabel}</small>}
                  {item.note && <small className="cash-item__note">{item.note}</small>}
                  {/* Renglones: qué se compró (insumo · cantidad unidad × costo unitario) o de qué tipo es el gasto. */}
                  {item.lines && item.lines.length > 0 && (
                    <ul className="cash-item__lines">
                      {item.lines.map((line, index) => {
                        const detail = describeCashSubLine(line);
                        return (
                          <li className="cash-line" key={`${line.label}-${index}`}>
                            <span className="cash-line__label">{line.label}{detail && <span className="cash-line__detail">{detail}</span>}</span>
                            <span className="cash-line__amount">{formatMoney(line.amount)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </span>
              </span>
              <strong>{formatMoney(item.amount)}</strong>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
